import { useEffect, useState } from "react"
import { BalanceType, BetSlipFragment, BetTypeEnum, StakeOrderFragment } from "../graphql/core-types"
import { calculateSlipMultiplier } from "./bet-line-helpers"

// made up stakers for the url builder's preview, so a streamer can see how a staked bet looks before anyone stakes.
// no avatar urls, they show as initials
const SAMPLE_STAKERS = [
  { name: 'NightOwl', amount: '15.00' },
  { name: 'ZeroCool', amount: '5.00' },
  { name: 'PixelPete', amount: '25.00' },
  { name: 'Marigold', amount: '10.00' },
  { name: 'Lazarus', amount: '5.00' },
  { name: 'Quokka', amount: '20.00' },
  { name: 'Brightside', amount: '10.00' },
]

// one staker arrives at a time, so the preview shows each avatar popping in the way a real stake would
const SAMPLE_STAKER_INTERVAL_MS = 1500

// the sample stakers need a bet to back when the streamer has no pending one. only the fields the bet card reads
export const SAMPLE_PENDING_BET_SLIP = {
  id: 'url-builder-sample-staked-bet',
  status: 'pending',
  insertedAt: new Date().toISOString(),
  betTotalAmount: '10.00',
  payoutAmounts: [],
  betItems: [{
    id: 'url-builder-sample-staked-line',
    gameSlug: 'fortnite',
    betType: BetTypeEnum.SINGLE_GAME_ITEM,
    title: 'kills',
    linesValue: '5',
    betAmount: '10.00',
    payoutMultiplier: '2.5',
    resolved: false,
    fundingSources: [{ wallet: BalanceType.PL_BALANCE, amount: '10.00' }],
  }],
} as unknown as BetSlipFragment

const sampleStakeOrder = ({ name, amount }: typeof SAMPLE_STAKERS[number], index: number): StakeOrderFragment => ({
  id: `url-builder-sample-stake-${index}`,
  createdAt: new Date(index * 1000).toISOString(),
  payout: null,
  user: { id: `url-builder-sample-staker-${index}`, loungeUsername: name, platformIdentifiers: [] },
  fundingSource: [{ wallet: BalanceType.PL_BALANCE, amount }],
}) as unknown as StakeOrderFragment

// swaps any real stakers for the first `count` sample ones. the offer pays out at the bet's own multiplier
export const withSampleStakers = (betSlip: BetSlipFragment, count: number): BetSlipFragment => {
  const multiplier = parseFloat(calculateSlipMultiplier(betSlip) ?? '') || 1
  return {
    ...betSlip,
    stakeOffer: {
      id: 'url-builder-sample-stake-offer',
      status: 'open',
      reward: null,
      maximumMultiplier: Math.round(multiplier * 1000),
      orders: SAMPLE_STAKERS.slice(0, count).map(sampleStakeOrder),
    },
  } as BetSlipFragment
}

// how many sample stakers are in so far, counting up to all of them and then holding
export const useSampleStakerCount = (isEnabled: boolean): number => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isEnabled) return
    const interval = setInterval(() => {
      setCount((current) => {
        if (current + 1 >= SAMPLE_STAKERS.length) clearInterval(interval)
        return Math.min(current + 1, SAMPLE_STAKERS.length)
      })
    }, SAMPLE_STAKER_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isEnabled])

  return count
}
